import {
  Alert,
  CustomInput,
  DatePicker,
  TextField,
  Toggle,
} from '@globalmed/atlas-ui';
import { memo, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FormBuilderElement.module.css';
import {
  FormBuilderCheckBox,
  FormBuilderLabel,
  FormBuilderDropdown,
  FormBuilderOptions,
} from './components';
import { Line } from '@/components/Line';
import { UploadFile } from '@/components/UploadFile';

type TProps = {
  label?: ReactNode;
  labelId?: string;
  options?: ReactNode;
  required?: boolean;
  onRequiredChange?: (required: boolean) => void;
  disabled?: boolean;
  alert?: string;
};

const FormBuilderElementComponent = memo(
  ({
    label,
    labelId,
    options,
    children,
    alert,
    required,
    disabled = false,
    onRequiredChange,
  }: PropsWithChildren<TProps>) => {
    const { t } = useTranslation();

    const isShowRequiredOption = required !== undefined || !!onRequiredChange;
    return (
      <div className={styles.container}>
        {!!alert && (
          <Alert sx={{ maxWidth: '100%' }} type="informative">
            {alert}
          </Alert>
        )}
        <div className={styles.labelWrapper}>
          {label}
          {children}
        </div>
        {options &&
          (labelId ? (
            <div role="group" aria-labelledby={labelId}>
              {options}
            </div>
          ) : (
            options
          ))}
        {!disabled && isShowRequiredOption && (
          <>
            <Line />
            <Toggle
              checked={required}
              onChange={(e) => onRequiredChange?.(e.target.checked)}
              label={t('admin-panel.required')}
              aria-describedby={labelId}
            />
          </>
        )}
      </div>
    );
  },
);

FormBuilderElementComponent.displayName = 'FormBuilderElement';

const FormBuilderElement = Object.assign(FormBuilderElementComponent, {
  SingleLineInput: CustomInput,
  MultiLineInput: TextField,
  UploadFile: UploadFile,
  Date: DatePicker,
  Checkbox: FormBuilderCheckBox,
  Dropdown: FormBuilderDropdown,
  Label: FormBuilderLabel,
  Options: FormBuilderOptions,
});

export { FormBuilderElement };
